/**
 * The viewer's preferred locale — the browser's language setting
 * (`navigator.language`), `undefined` when no navigator exists (tests, SSR) so
 * `Intl` falls back to the runtime default. Non-flight display passes this to
 * `toLocaleString` so the 12/24-hour clock follows the device (issue #165);
 * flight display deliberately does NOT — it pins the 24-hour aviation clock
 * (issue #164).
 */
export const deviceLocale = (): string | undefined =>
	typeof navigator !== 'undefined' ? navigator.language : undefined

/**
 * Does the viewer's locale read the clock in 24-hour form? Display can simply
 * hand the locale to `Intl` and let it decide, but *inputs* have to be told:
 * the pickers take an explicit 12/24 flag, and issue #164 is emphatic that the
 * picker and the display must agree ("Both halves must change together, or we
 * just recreate the mismatch in reverse"). Derived from `Intl` rather than a
 * hand-kept locale list so it tracks the platform's own data.
 */
export const deviceUses24HourClock = (): boolean => {
	// `hourCycle` is in the ES2020+ runtime but not in this project's TS lib
	// typings for ResolvedDateTimeFormatOptions, hence the widening.
	const resolved = new Intl.DateTimeFormat(deviceLocale(), {
		hour: 'numeric',
	}).resolvedOptions() as Intl.ResolvedDateTimeFormatOptions & { hourCycle?: string }
	// `hour12` is the direct answer where the runtime reports it; `hourCycle`
	// ('h23'/'h24' are the 24-hour cycles) is the fallback.
	if (typeof resolved.hour12 === 'boolean') return !resolved.hour12
	return resolved.hourCycle === 'h23' || resolved.hourCycle === 'h24'
}
